import { apiClient } from './api';

// Get SOW insights for a project
export const getSowInsights = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/sow-insights`);
    return response.data;
  } catch (error) {
    console.error('Error fetching SOW insights:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch project document insights',
      sow_insights: null,
      insights: {
        summary: null,
        requirements: [],
        key_features: [],
        timeline: [],
        stakeholders: [],
        risks: []
      },
      has_insights: false
    };
  }
};

// Update SOW insights for a project
export const updateSowInsights = async (projectId, insights) => {
  try {
    const response = await apiClient.post(`/projects/${projectId}/sow-insights`, {
      insights: insights
    });
    return response.data;
  } catch (error) {
    console.error('Error updating SOW insights:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update project document insights'
    };
  }
};

// Generate insights using AI and automatically save to database
export const generateAndSaveSowInsights = async (projectId) => {
  try {
    // Call the existing AI extraction endpoint which now saves to database
    const response = await apiClient.post(`/ai/extract-sow-insights/${projectId}`);
    
    if (response.data.success) {
      // The insights are now automatically saved to database by the backend
      return {
        success: true,
        insights: response.data.insights,
        sow_document: response.data.sow_document,
        message: 'Insights generated and saved successfully'
      };
    } else {
      return {
        success: false,
        error: response.data.error || 'Failed to generate insights'
      };
    }
  } catch (error) {
    console.error('Error generating SOW insights:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'Failed to generate project document insights'
    };
  }
};

// Delete SOW insights for a project
export const deleteSowInsights = async (projectId) => {
  try {
    const response = await apiClient.delete(`/projects/${projectId}/sow-insights`);
    return response.data;
  } catch (error) {
    console.error('Error deleting SOW insights:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to delete project document insights'
    };
  }
};

// Export SOW insights as JSON
export const exportSowInsights = async (projectId) => {
  try {
    const response = await apiClient.get(`/projects/${projectId}/sow-insights/export`);
    return response.data;
  } catch (error) {
    console.error('Error exporting SOW insights:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to export project document insights'
    };
  }
};

// Get insights summary for all projects (admin only)
export const getInsightsSummary = async () => {
  try {
    const response = await apiClient.get('/admin/sow-insights/summary');
    return response.data;
  } catch (error) {
    console.error('Error fetching insights summary:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch insights summary',
      insights_summary: [],
      total_projects_with_insights: 0
    };
  }
};

// Save insights automatically when data changes (debounced)
let saveTimeout = null;
export const autoSaveSowInsights = (projectId, insights, delay = 2000) => {
  // Clear existing timeout
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }
  
  // Set new timeout for auto-save
  saveTimeout = setTimeout(async () => {
    try {
      const result = await updateSowInsights(projectId, insights);
      if (result.success) {
        console.log('SOW insights auto-saved successfully');
      } else {
        console.warn('Auto-save failed:', result.error);
      }
    } catch (error) {
      console.error('Auto-save error:', error);
    }
  }, delay);
};

// Clear auto-save timeout (call when component unmounts)
export const clearAutoSave = () => {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
    saveTimeout = null;
  }
};
